/* global GamePix */
import React, { useState, useEffect, useRef, useCallback } from 'react';

const SpaceLaxer = () => {
  const [screen, setScreen] = useState('menu');
  const [paused, setPaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [showBlastAnimation, setShowBlastAnimation] = useState(false);
  const [bullets, setBullets] = useState([]);
  const [enemyBullets, setEnemyBullets] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [aliens, setAliens] = useState([]);
  const [asteroids, setAsteroids] = useState([]);
  const [stars, setStars] = useState([]);
  const [bgElements, setBgElements] = useState([]);
  const [particles, setParticles] = useState([]);
  const [score, setScore] = useState(0);
  const [kills, setKills] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [bestKills, setBestKills] = useState(0);
  const [distance, setDistance] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [gameSpeed, setGameSpeed] = useState(0.8);
  const [bulletSpeed, setBulletSpeed] = useState(2);
  const [laxifierBubble, setLaxifierBubble] = useState(null);
  const [laxifierHitsNeeded, setLaxifierHitsNeeded] = useState(3);
  const [laxifierActive, setLaxifierActive] = useState(false);
  const [laxifierTimer, setLaxifierTimer] = useState(0);
  const [rampageBubble, setRampageBubble] = useState(null);
  const [rampageActive, setRampageActive] = useState(false);
  const [rampageTimer, setRampageTimer] = useState(0);
  const [wrapBubble, setWrapBubble] = useState(null);
  const [wrapActive, setWrapActive] = useState(false);
  const [blueTheme, setBlueTheme] = useState(false);
  const [musicVolume, setMusicVolume] = useState(0.78); // 30% increase from 0.6 = 0.78
  const [fxVolume, setFxVolume] = useState(0.7); // Base FX volume (30% decrease applied per sound)
  const [menuPlanePos, setMenuPlanePos] = useState({ x: 50, y: 50, vx: 0.3, vy: 0.2 });
  // New features
  const [playerHealth, setPlayerHealth] = useState(3);
  const [boss, setBoss] = useState(null);
  const [bossWarning, setBossWarning] = useState(false);
  const [lastBossScore, setLastBossScore] = useState(0);  // Track score at last boss spawn
  const [bossCount, setBossCount] = useState(0);  // Track number of bosses defeated
  const [waveActive, setWaveActive] = useState(false);
  const [lastWaveTime, setLastWaveTime] = useState(0);
  const [baseFiringSpeed, setBaseFiringSpeed] = useState(1.0);  // Increases 10% every 400 score
  const [introAsteroids, setIntroAsteroids] = useState([]);
  // GamePix SDK states
  const [showReviveModal, setShowReviveModal] = useState(false);
  const [adLoading, setAdLoading] = useState(false);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const [gameOverSelection, setGameOverSelection] = useState('retry'); // 'retry' or 'menu'
  const gameAreaRef = useRef(null);
  const touchStartX = useRef(null);
  const planeXRef = useRef(50);
  const laxifierHitsRef = useRef(0);
  const animationFrameRef = useRef(null);
  const uniqueIdCounter = useRef(0);

  const getUniqueId = useCallback(() => {
    uniqueIdCounter.current += 1;
    return `${Date.now()}-${uniqueIdCounter.current}`;
  }, []);

  // Audio — JS Audio objects with relative paths (works in any iframe/hosting env)
  const audioRef = useRef(null);
  const shootSoundRef = useRef(null);
  const enemyDestroySoundRef = useRef(null);
  const playerHitSoundRef = useRef(null);
  const gameOverSoundRef = useRef(null);
  const abilityCollectSoundRef = useRef(null);
  const bossWarningSoundRef = useRef(null);

  const PLANE_SIZE = 50;
  const BUBBLE_SIZE = 60;
  const ALIEN_SIZE = 50;
  const ASTEROID_BASE_SIZE = 70;
  const LANES = [15, 30, 45, 60, 75];
  const MAX_PARTICLES = 60;  // Optimized: reduced from 100 to 60 for better performance
  const MAX_BULLETS = 50;

  const theme = blueTheme ? {
    bgGradient: 'from-blue-950 via-indigo-900 to-purple-900',
    bgSolid: 'bg-indigo-950',
    plane: '#60a5fa',
    planeDark: '#3b82f6',
    enemy: '#ff6b9d',
    enemyDark: '#ff1744',
    alien: '#60a5fa',
    alienDark: '#1e3a8a',
    bullet: '#38bdf8',
    bulletGlow: '#0ea5e9',
    text: 'text-cyan-100',
    border: 'border-cyan-400',
    button: 'bg-cyan-500 hover:bg-cyan-600 text-white',
    buttonSecondary: 'bg-purple-600 border-2 border-cyan-400 text-cyan-100 hover:bg-purple-700',
    star: '#fbbf24',
    rampage: '#f59e0b',
    asteroid: '#ff6b35',
    laxifier: '#ffd700',
    wrap: '#00ffff'
  } : {
    bgGradient: 'from-gray-900 via-slate-900 to-zinc-900',
    bgSolid: 'bg-slate-950',
    plane: '#f0f0f0',
    planeDark: '#b0b0b0',
    enemy: '#ff4d4d',
    enemyDark: '#cc0000',
    alien: '#60a5fa',
    alienDark: '#1e3a8a',
    bullet: '#ffeb3b',
    bulletGlow: '#ffc107',
    text: 'text-white',
    border: 'border-amber-400',
    button: 'bg-white hover:bg-gray-200 text-black',
    buttonSecondary: 'bg-zinc-800 border-2 border-white text-white hover:bg-zinc-700',
    star: '#fbbf24',
    rampage: '#ff5722',
    asteroid: '#ff6b35',
    laxifier: '#ffd700',
    wrap: '#00ffff'
  };


  // Helper function to update score and notify GamePix
  const updateScore = useCallback((scoreUpdate) => {
    setScore(prev => {
      const newScore = typeof scoreUpdate === 'function' ? scoreUpdate(prev) : scoreUpdate;
      // Notify GamePix of score update (only if SDK is loaded)
      if (typeof window !== 'undefined' && window.GamePix && typeof window.GamePix.updateScore === 'function') {
        try {
          window.GamePix.updateScore(newScore);
        } catch (error) {
          console.error('Failed to update GamePix score:', error);
        }
      }
      return newScore;
    });
  }, []);
  useEffect(() => {
    planeXRef.current = 50;

    // Initialize Audio objects with RELATIVE paths (works in any iframe/hosting env)
    // Cannot use /sounds/ (absolute) — it resolves to the host domain, not the game folder
    audioRef.current = new Audio('./sounds/bgm.mp3');
    audioRef.current.loop = true;
    audioRef.current.volume = musicVolume; // Use state volume

    shootSoundRef.current = new Audio('./sounds/shoot.mp3');
    enemyDestroySoundRef.current = new Audio('./sounds/explosion.mp3');
    playerHitSoundRef.current = new Audio('./sounds/hit.mp3');
    gameOverSoundRef.current = new Audio('./sounds/gameover.mp3');
    abilityCollectSoundRef.current = new Audio('./sounds/powerup.mp3');
    bossWarningSoundRef.current = new Audio('./sounds/boss-warning.mp3');

    // Load saved stats using GamePix.localStorage (with safety check)
    if (typeof window !== 'undefined' && window.GamePix && window.GamePix.localStorage) {
      try {
        const saved = window.GamePix.localStorage.getItem('spacelaxer-stats');
        if (saved) {
          const stats = JSON.parse(saved);
          setBestScore(stats.bestScore || 0);
          setBestKills(stats.bestKills || 0);
        }

        // Load audio settings
        const audioSettings = window.GamePix.localStorage.getItem('spacelaxer-audio');
        if (audioSettings) {
          const settings = JSON.parse(audioSettings);
          if (settings.musicVolume !== undefined) setMusicVolume(settings.musicVolume);
          if (settings.fxVolume !== undefined) setFxVolume(settings.fxVolume);
        }
      } catch (error) {
        console.error('Failed to load saved settings:', error);
      }
    }

    // GamePix SDK is auto-initialized when script loads
    if (window.GamePix) {
      setSdkInitialized(true);
      console.log('GamePix SDK is available');
    } else {
      console.log('GamePix SDK not available (running locally)');
    }

    // Prevent unwanted page scrolling (GamePix requirement)
    const preventScroll = (event) => {
      if (["ArrowUp", "ArrowDown", " "].includes(event.key)) {
        event.preventDefault();
      }
    };
    
    const preventWheel = (event) => event.preventDefault();

    window.addEventListener("keydown", preventScroll);
    window.addEventListener("wheel", preventWheel, { passive: false });

    return () => {
      window.removeEventListener("keydown", preventScroll);
      window.removeEventListener("wheel", preventWheel, { passive: false });
      // Clean up audio
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  // Update BGM volume when musicVolume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = musicVolume;
    }
  }, [musicVolume]);

  // Save audio settings to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && window.GamePix && window.GamePix.localStorage) {
      try {
        window.GamePix.localStorage.setItem('spacelaxer-audio', JSON.stringify({
          musicVolume,
          fxVolume
        }));
      } catch (error) {
        console.error('Failed to save audio settings:', error);
      }
    }
  }, [musicVolume, fxVolume]);

  const createParticles = useCallback((x, y, color, count = 8) => {
    const newParticles = Array.from({ length: count }, (_, i) => ({
      id: `particle-${Date.now()}-${i}-${Math.random()}`,
      x: x,
      y: y,
      vx: (Math.random() - 0.5) * 4,
      vy: (Math.random() - 0.5) * 4,
      color: color,
      life: 30  // Optimized to 30 frames (~0.5s) for proper fade-out
    }));
    setParticles(prev => [...prev.slice(-MAX_PARTICLES + count), ...newParticles]);
  }, []);

  // Sound playing functions
  const playSound = useCallback((soundRef, baseVolume = 0.5) => {
    if (soundRef.current) {
      try {
        soundRef.current.volume = baseVolume * fxVolume; // Apply FX volume setting
        soundRef.current.currentTime = 0; // Reset to start
        soundRef.current.play().catch(e => {
          // Silently handle autoplay restrictions
          if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') {
            console.log('Sound play failed:', e.name);
          }
        });
      } catch (error) {
        // Silently handle errors
      }
    }
  }, [fxVolume]);

  const playShootSound = useCallback(() => playSound(shootSoundRef, 0.21), [playSound]); // 30% quieter
  const playEnemyDestroySound = useCallback(() => playSound(enemyDestroySoundRef, 0.4), [playSound]);
  const playPlayerHitSound = useCallback(() => playSound(playerHitSoundRef, 0.5), [playSound]);
  const playGameOverSound = useCallback(() => playSound(gameOverSoundRef, 0.6), [playSound]);
  const playAbilityCollectSound = useCallback(() => playSound(abilityCollectSoundRef, 0.5), [playSound]);
  const playBossWarningSound = useCallback(() => playSound(bossWarningSoundRef, 0.7), [playSound]);

  useEffect(() => {
    if (screen === 'game' && stars.length === 0) {
      const newStars = Array.from({ length: 30 }, (_, i) => ({
        id: `star-${i}`,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 2 + 1
      }));
      setStars(newStars);
    }
  }, [screen, stars.length]);

  // Play game over sound when revive modal shows
  useEffect(() => {
    if (showReviveModal) {
      playGameOverSound();
    }
  }, [showReviveModal, playGameOverSound]);

  useEffect(() => {
    if (screen !== 'menu') return;
    const interval = setInterval(() => {
      setMenuPlanePos(prev => {
        let newX = prev.x + prev.vx;
        let newY = prev.y + prev.vy;
        let newVx = prev.vx;
        let newVy = prev.vy;

        if (newX <= 10 || newX >= 90) newVx = -prev.vx;
        if (newY <= 10 || newY >= 90) newVy = -prev.vy;

        if (Math.random() > 0.98) newVx = (Math.random() - 0.5) * 0.6;
        if (Math.random() > 0.98) newVy = (Math.random() - 0.5) * 0.6;

        return {
          x: Math.max(10, Math.min(90, newX)),
          y: Math.max(10, Math.min(90, newY)),
          vx: newVx,
          vy: newVy
        };
      });
    }, 30);
    return () => clearInterval(interval);
  }, [screen]);

  useEffect(() => {
    if (screen !== 'game' || paused) return;
    const spawnRate = Math.max(1000, 3000 / gameSpeed);
    const interval = setInterval(() => {
      if (gameSpeed > 0.8 && Math.random() > 0.7) {
        const type = gameSpeed > 1.2 ? ['planet', 'galaxy', 'shootingStar'][Math.floor(Math.random() * 3)] : 
                     gameSpeed > 1.0 ? ['planet', 'shootingStar'][Math.floor(Math.random() * 2)] : 'planet';
        
        setBgElements(prev => [...prev.slice(-10), {
          id: `bg-${Date.now()}`,
          type: type,
          x: Math.random() * 90 + 5,
          y: -10,
          size: type === 'galaxy' ? 60 : type === 'planet' ? 40 : 20,
          color: type === 'planet' ? ['#ff6b9d', '#60a5fa', '#a78bfa'][Math.floor(Math.random() * 3)] : '#fff',
          hasRings: type === 'planet' && Math.random() > 0.6,
          rotation: Math.random() * 360
        }]);
      }
    }, spawnRate);
    return () => clearInterval(interval);
  }, [screen, gameSpeed, paused]);

  useEffect(() => {
    if (screen !== 'game' || gameSpeed < 1.0 || paused || boss) return;  // Added boss check
    const spawnRate = Math.max(2000, 4000 / gameSpeed);
    const interval = setInterval(() => {
      // Spawn probability increases over time: starts at 40%, goes up to 80%
      const spawnThreshold = Math.max(0.2, 0.6 - (gameSpeed - 1.0) * 0.15);
      if (Math.random() > spawnThreshold) {
        const lane = LANES[Math.floor(Math.random() * LANES.length)];
        setAsteroids(prev => [...prev, { 
          id: Date.now(), 
          x: lane, 
          y: -10,
          spawnTime: Date.now(),
          speed: 1.2 + Math.random() * 0.8
        }]);
      }
    }, spawnRate);
    return () => clearInterval(interval);
  }, [screen, gameSpeed, paused, boss]);

  useEffect(() => {
    if (screen !== 'game' || paused) return;
    const baseSpeed = rampageActive ? 200 : 333;
    const shootSpeed = (baseSpeed / gameSpeed) / baseFiringSpeed;  // Apply firing speed multiplier
    
    const interval = setInterval(() => {
      const currentX = planeXRef.current;
      
      setBullets(prev => {
        const newBullets = [];
        
        if (rampageActive && laxifierActive) {
          // 5 multidirectional bullets
          const angles = [-60, -30, 0, 30, 60];
          angles.forEach((angle) => {
            newBullets.push({
              id: getUniqueId(),
              x: currentX,
              y: 85,
              angle: angle,
              destroyed: false
            });
          });
        } else if (rampageActive) {
          newBullets.push(
            { id: getUniqueId(), x: currentX, y: 85, angle: -45, destroyed: false },
            { id: getUniqueId(), x: currentX, y: 85, angle: 0, destroyed: false },
            { id: getUniqueId(), x: currentX, y: 85, angle: 45, destroyed: false }
          );
        } else if (laxifierActive) {
          newBullets.push(
            { id: getUniqueId(), x: currentX - 3, y: 85, angle: 0, destroyed: false },
            { id: getUniqueId(), x: currentX, y: 85, angle: 0, destroyed: false },
            { id: getUniqueId(), x: currentX + 3, y: 85, angle: 0, destroyed: false }
          );
        } else {
          newBullets.push({ id: getUniqueId(), x: currentX, y: 85, angle: 0, destroyed: false });
        }
        
        return [...prev.slice(-MAX_BULLETS + newBullets.length), ...newBullets];
      });
      
      // Play shoot sound
      playShootSound();
    }, shootSpeed);
    return () => clearInterval(interval);
  }, [screen, laxifierActive, rampageActive, gameSpeed, paused, baseFiringSpeed, getUniqueId, playShootSound]);

  useEffect(() => {
    if (screen !== 'game' || paused || boss) return;  // Added boss check
    const spawnRate = Math.max(1200, 2000 / gameSpeed);
    const interval = setInterval(() => {
      // Spawn probability increases over time: starts at 70%, goes up to 100%
      const spawnThreshold = Math.max(0.0, 0.3 - (gameSpeed - 0.6) * 0.15);
      if (Math.random() > spawnThreshold) {
        const lane = LANES[Math.floor(Math.random() * LANES.length)];
        // Enemy health: 1-3 hits based on game speed
        const enemyHealth = gameSpeed > 1.2 ? Math.floor(1 + Math.random() * 2.99) : 1;
        setEnemies(prev => [...prev, { 
          id: Date.now(), 
          x: lane, 
          y: -10, 
          lastShot: 0,
          health: enemyHealth,
          maxHealth: enemyHealth
        }]);
      }
    }, spawnRate);
    return () => clearInterval(interval);
  }, [screen, gameSpeed, paused, boss]);

  // Firing speed progression - increases 10% every 400 score
  useEffect(() => {
    if (screen !== 'game') return;
    const firingSpeedMultiplier = 1.0 + Math.floor(score / 400) * 0.1;
    setBaseFiringSpeed(firingSpeedMultiplier);
  }, [screen, score]);

  useEffect(() => {
    if (screen !== 'game' || paused || boss) return;  // Added boss check
    const spawnRate = Math.max(1800, 3000 / gameSpeed);
    const interval = setInterval(() => {
      // Spawn probability increases over time: starts at 50%, goes up to 90%
      const spawnThreshold = Math.max(0.1, 0.5 - (gameSpeed - 0.6) * 0.2);
      if (Math.random() > spawnThreshold) {
        const lane = LANES[Math.floor(Math.random() * LANES.length)];
        setAliens(prev => [...prev, { id: Date.now(), x: lane, y: -10, lastShot: 0 }]);
      }
    }, spawnRate);
    return () => clearInterval(interval);
  }, [screen, gameSpeed, paused, boss]);

  useEffect(() => {
    if (screen !== 'game' || paused) return;
    const spawnRate = Math.max(8000, 12000 / gameSpeed);
    const interval = setInterval(() => {
      if (!laxifierBubble) {
        // Base probability: 50%, Boss fight: +40%, Speed bonus: +10% per speed unit
        const baseProbability = 0.5;
        const bossBonus = boss ? 0.4 : 0;  // Increased from 0.3 to 0.4 (40%)
        const speedBonus = Math.min(gameSpeed * 0.1, 0.3); // Capped at +30%
        const totalProbability = baseProbability + bossBonus + speedBonus;
        
        if (Math.random() < totalProbability) {
          const lane = LANES[Math.floor(Math.random() * LANES.length)];
          const hitsNeeded = gameSpeed > 1.5 ? Math.floor(Math.random() * 6) + 5 : Math.floor(Math.random() * 4) + 2;
          setLaxifierHitsNeeded(hitsNeeded);
          laxifierHitsRef.current = 0;
          setLaxifierBubble({ x: lane, y: -10, hitsNeeded });
        }
      }
    }, spawnRate);
    return () => clearInterval(interval);
  }, [screen, laxifierBubble, gameSpeed, paused, boss]);

  useEffect(() => {
    if (screen !== 'game' || paused) return;
    const spawnRate = Math.max(10000, 15000 / gameSpeed);
    const interval = setInterval(() => {
      if (!rampageBubble) {
        // Base probability: 40%, Boss fight: +40%, Speed bonus: +10% per speed unit
        const baseProbability = 0.4;
        const bossBonus = boss ? 0.4 : 0;  // Increased from 0.3 to 0.4 (40%)
        const speedBonus = Math.min(gameSpeed * 0.1, 0.3); // Capped at +30%
        const totalProbability = baseProbability + bossBonus + speedBonus;
        
        if (Math.random() < totalProbability) {
          const lane = LANES[Math.floor(Math.random() * LANES.length)];
          setRampageBubble({ x: lane, y: -10 });
        }
      }
    }, spawnRate);
    return () => clearInterval(interval);
  }, [screen, rampageBubble, gameSpeed, paused, boss]);

  useEffect(() => {
    if (screen !== 'game' || paused) return;
    const spawnRate = Math.max(13000, 18000 / gameSpeed);
    const interval = setInterval(() => {
      if (!wrapBubble) {
        // Base probability: 30%, Boss fight: +40%, Speed bonus: +10% per speed unit
        const baseProbability = 0.3;
        const bossBonus = boss ? 0.4 : 0;  // Increased from 0.3 to 0.4 (40%)
        const speedBonus = Math.min(gameSpeed * 0.1, 0.3); // Capped at +30%
        const totalProbability = baseProbability + bossBonus + speedBonus;
        
        if (Math.random() < totalProbability) {
          const lane = LANES[Math.floor(Math.random() * LANES.length)];
          setWrapBubble({ x: lane, y: -10 });
        }
      }
    }, spawnRate);
    return () => clearInterval(interval);
  }, [screen, wrapBubble, gameSpeed, paused, boss]);

  // Menu screen asteroids animation
  useEffect(() => {
    if (screen !== 'menu') return;
    
    // Initialize asteroids if empty
    if (introAsteroids.length === 0) {
      const initialAsteroids = Array.from({ length: 8 }, (_, i) => ({
        id: `intro-ast-${i}`,
        x: Math.random() * 90 + 5,
        y: Math.random() * 120 - 20,
        speed: 0.3 + Math.random() * 0.4,
        size: 40 + Math.random() * 30,
        rotation: Math.random() * 360
      }));
      setIntroAsteroids(initialAsteroids);
    }
    
    const interval = setInterval(() => {
      setIntroAsteroids(prev => prev.map(ast => {
        const newY = ast.y + ast.speed;
        return newY > 120 ? {
          ...ast,
          y: -20,
          x: Math.random() * 90 + 5
        } : {
          ...ast,
          y: newY,
          rotation: ast.rotation + 0.5
        };
      }));
    }, 16);
    
    return () => clearInterval(interval);
  }, [screen, introAsteroids.length]);

  useEffect(() => {
    if (!laxifierActive || paused) return;
    const interval = setInterval(() => {
      setLaxifierTimer(prev => {
        if (prev <= 100) {
          setLaxifierActive(false);
          return 0;
        }
        return prev - 100;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [laxifierActive, paused]);

  useEffect(() => {
    if (!rampageActive || paused) return;
    const interval = setInterval(() => {
      setRampageTimer(prev => {
        if (prev <= 100) {
          setRampageActive(false);
          return 0;
        }
        return prev - 100;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [rampageActive, paused]);

  // Boss fight trigger - every 240 score (20% increase from 200)
  useEffect(() => {
    if (screen !== 'game' || paused || boss || bossWarning) return;
    
    const scoreThreshold = lastBossScore + 240; // Every 240 score
    if (score >= scoreThreshold) {
      // Show warning and make player immune
      setBossWarning(true);
      playBossWarningSound();  // Sound effect
      // Player is immune during warning (checked in collision detection)
      
      setTimeout(() => {
        setBossWarning(false);
        
        // Determine boss type (alternating or random)
        const bossType = Math.random() > 0.5 ? 'laxitor' : 'rampagant';
        
        // Spawn boss - health scales with boss count
        const bossHealth = Math.floor(15 + bossCount * 5); // 15, 20, 25, 30...
        const bossFiringSpeed = 1.0 + (bossCount * 0.2); // Increases 20% per boss
        
        setBoss({
          id: Date.now(),
          x: 50,
          y: 10,
          health: bossHealth,
          maxHealth: bossHealth,
          direction: 1,
          lastShot: 0,
          type: bossType,  // 'laxitor' or 'rampagant'
          firingSpeed: bossFiringSpeed
        });
        setLastBossScore(score);
      }, 3000); // 3 second warning
    }
  }, [screen, paused, score, boss, bossWarning, lastBossScore, bossCount]);

  // Wave spawning - every 15 seconds
  useEffect(() => {
    if (screen !== 'game' || paused || boss) return;
    
    const now = Date.now();
    if (now - lastWaveTime > 15000 && gameSpeed >= 0.8) {
      setWaveActive(true);
      setLastWaveTime(now);
      
      // Spawn wave of enemies
      const waveSize = Math.floor(3 + gameSpeed);
      const waveEnemies = [];
      
      for (let i = 0; i < waveSize; i++) {
        setTimeout(() => {
          const lane = LANES[Math.floor(Math.random() * LANES.length)];
          const enemyHealth = Math.floor(1 + Math.random() * 2); // 1-3 health
          setEnemies(prev => [...prev, { 
            id: Date.now() + i, 
            x: lane, 
            y: -10 - (i * 15), 
            lastShot: 0,
            health: enemyHealth,
            maxHealth: enemyHealth
          }]);
        }, i * 300);
      }
      
      setTimeout(() => setWaveActive(false), 2000);
    }
  }, [screen, paused, boss, lastWaveTime, gameSpeed]);

  // Boss movement and shooting
  useEffect(() => {
    if (!boss || screen !== 'game' || paused) return;
    
    const interval = setInterval(() => {
      setBoss(prev => {
        if (!prev) return null;
        
        // Move left and right
        let newX = prev.x + prev.direction * 0.5;
        let newDirection = prev.direction;
        
        if (newX <= 15) {
          newX = 15;
          newDirection = 1;
        } else if (newX >= 85) {
          newX = 85;
          newDirection = -1;
        }
        
        // Boss shooting based on type
        const now = Date.now();
        const baseFireRate = 800 / prev.firingSpeed;  // Faster with each boss (20% per boss)
        let newLastShot = prev.lastShot;
        
        if (now - prev.lastShot > baseFireRate) {
          if (prev.type === 'laxitor') {
            // Laxitor: fires 3 STRAIGHT bullets (laxifier pattern)
            setEnemyBullets(eb => [...eb, 
              { id: Date.now(), x: prev.x - 5, y: prev.y + 10, angle: 0, isBossBullet: true },
              { id: Date.now() + 1, x: prev.x, y: prev.y + 10, angle: 0, isBossBullet: true },
              { id: Date.now() + 2, x: prev.x + 5, y: prev.y + 10, angle: 0, isBossBullet: true }
            ]);
            newLastShot = now;
          } else if (prev.type === 'rampagant') {
            // Rampagant: fires 3 ANGLED bullets (rampage pattern)
            const angles = [-30, 0, 30];
            setEnemyBullets(eb => [...eb,
              { id: Date.now(), x: prev.x, y: prev.y + 10, angle: -30, isBossBullet: true },
              { id: Date.now() + 1, x: prev.x, y: prev.y + 10, angle: 0, isBossBullet: true },
              { id: Date.now() + 2, x: prev.x, y: prev.y + 10, angle: 30, isBossBullet: true }
            ]);
            newLastShot = now;
          }
        }
        
        return { ...prev, x: newX, direction: newDirection, lastShot: newLastShot };
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, [boss, screen, paused]);

  useEffect(() => {
    if (screen !== 'game' || paused) return;
    
    const loop = setInterval(() => {
      setScrollOffset(prev => (prev + 2 * gameSpeed) % 100);
      setDistance(prev => {
        const newDist = prev + gameSpeed;
        if (Math.floor(newDist / 1000) > Math.floor(prev / 1000)) {
          setGameSpeed(s => s + 0.05); // Increased from variable increment to constant 0.05
          setBulletSpeed(bs => Math.min(bs + 0.2, 10));
        }
        return newDist;
      });
      
      if (distance % 100 === 0 && distance > 0) updateScore(prev => prev + 10);

      setStars(prev => prev.map(s => ({ ...s, y: (s.y + 0.5 * gameSpeed) % 105 })));
      setBgElements(prev => prev.map(e => ({
        ...e,
        y: e.type === 'shootingStar' ? e.y + 2 * gameSpeed : e.y + 0.3 * gameSpeed
      })).filter(e => e.y < 110));

      setParticles(prev => prev.map(p => ({
        ...p,
        x: p.x + p.vx,
        y: p.y + p.vy,
        life: p.life - 1
      })).filter(p => p.life > 0 && p.x >= -10 && p.x <= 110 && p.y >= -10 && p.y <= 110));

      // ===== COLLISION DETECTION FIRST =====
      const planeX = planeXRef.current;
      const planeY = 85;

      setBullets(currentBullets => {
        // Mark bullets that hit enemies/aliens/asteroids/boss as destroyed
        const updatedBullets = currentBullets.map(b => ({ ...b }));
        
        setEnemies(currentEnemies => {
          const updatedEnemies = [];
          currentEnemies.forEach(e => {
            let hit = false;
            updatedBullets.forEach(b => {
              if (b.destroyed) return;
              if (Math.abs(b.x - e.x) < 8 && Math.abs(b.y - e.y) < 8) {
                b.destroyed = true;
                hit = true;
                createParticles(b.x, b.y, theme.bullet, 4);
                
                // Reduce enemy health
                e.health = (e.health || 1) - 1;
                if (e.health <= 0) {
                  createParticles(e.x, e.y, theme.enemy, 6);
                  playEnemyDestroySound();
                  updateScore(s => s + (e.maxHealth || 1) * 10);
                  setKills(k => k + 1);
                } else {
                  createParticles(e.x, e.y, theme.enemy, 3);
                }
              }
            });
            if (!hit || e.health > 0) updatedEnemies.push(e);
          });
          return updatedEnemies;
        });

        setAliens(currentAliens => {
          const updatedAliens = [];
          currentAliens.forEach(a => {
            let hit = false;
            updatedBullets.forEach(b => {
              if (b.destroyed) return;
              if (Math.abs(b.x - a.x) < 8 && Math.abs(b.y - a.y) < 8) {
                b.destroyed = true;
                hit = true;
                createParticles(b.x, b.y, theme.bullet, 4);
                createParticles(a.x, a.y, theme.alien, 6);
                playEnemyDestroySound();
                updateScore(s => s + 20);
                setKills(k => k + 1);
              }
            });
            if (!hit) updatedAliens.push(a);
          });
          return updatedAliens;
        });

        setAsteroids(currentAsteroids => {
          const updatedAsteroids = [];
          currentAsteroids.forEach(ast => {
            let hit = false;
            updatedBullets.forEach(b => {
              if (b.destroyed) return;
              if (Math.abs(b.x - ast.x) < 8 && Math.abs(b.y - ast.y) < 8) {
                b.destroyed = true;
                hit = true;
                createParticles(b.x, b.y, theme.bullet, 4);
                createParticles(ast.x, ast.y, theme.asteroid, 8);
                playEnemyDestroySound();
                updateScore(s => s + 30);
              }
            });
            if (!hit) updatedAsteroids.push(ast);
          });
          return updatedAsteroids;
        });

        if (boss) {
          updatedBullets.forEach(b => {
            if (b.destroyed) return;
            if (Math.abs(b.x - boss.x) < 15 && Math.abs(b.y - boss.y) < 15) {
              b.destroyed = true;
              setBoss(prev => {
                if (!prev) return null;
                const newHealth = prev.health - 1;
                createParticles(b.x, b.y, theme.bullet, 6);
                
                if (newHealth <= 0) {
                  createParticles(prev.x, prev.y, theme.enemy, 10);
                  playEnemyDestroySound();
                  updateScore(s => s + prev.maxHealth * 10);
                  setKills(k => k + 1);
                  setBossCount(c => c + 1);
                  return null;
                }
                return { ...prev, health: newHealth };
              });
            }
          });
        }

        if (laxifierBubble) {
          updatedBullets.forEach(b => {
            if (b.destroyed) return;
            if (Math.abs(b.x - laxifierBubble.x) < 8 && Math.abs(b.y - laxifierBubble.y) < 8) {
              b.destroyed = true;
              laxifierHitsRef.current += 1;
              if (laxifierHitsRef.current >= laxifierHitsNeeded) {
                createParticles(laxifierBubble.x, laxifierBubble.y, theme.laxifier, 15);
                playAbilityCollectSound();
                setLaxifierBubble(null);
                setLaxifierActive(true);
                setLaxifierTimer(8000);
                laxifierHitsRef.current = 0;
              } else {
                createParticles(laxifierBubble.x, laxifierBubble.y, theme.laxifier, 5);
              }
            }
          });
        }

        // Move bullets and filter out destroyed + out of bounds
        return updatedBullets.map(b => ({
          ...b,
          y: b.y - bulletSpeed,
          x: b.x + (b.angle ? Math.sin(b.angle * Math.PI / 180) * 3 : 0)
        })).filter(b => !b.destroyed && b.y > -10 && b.x > 0 && b.x < 100);
      });
      
      setEnemyBullets(prev => prev.map(b => ({
        ...b,
        y: b.y + 2,
        x: b.angle ? b.x + Math.sin(b.angle * Math.PI / 180) * 1.5 : b.x
      })).filter(b => b.y < 105 && b.x > 0 && b.x < 100));

      const now = Date.now();
      setEnemies(prev => prev.map(e => {
        const updated = { ...e, y: e.y + 0.6 };
        if (now - e.lastShot > 2000 && e.y > 10 && e.y < 80) {
          setEnemyBullets(eb => [...eb, { id: Date.now(), x: e.x, y: e.y + 5 }]);
          updated.lastShot = now;
        }
        return updated;
      }).filter(e => e.y < 105));

      setAliens(prev => prev.map(a => {
        const updated = { ...a, y: a.y + 0.5 };
        if (now - a.lastShot > 2500 && a.y > 10 && a.y < 80) {
          setEnemyBullets(eb => [...eb, { id: Date.now(), x: a.x, y: a.y + 5 }]);
          updated.lastShot = now;
        }
        return updated;
      }).filter(a => a.y < 105));

      setAsteroids(prev => prev.map(a => ({ ...a, y: a.y + a.speed })).filter(a => a.y < 105));

      if (laxifierBubble) {
        setLaxifierBubble(prev => {
          if (!prev) return null;
          const newY = prev.y + 0.5;
          if (newY > 105) return null;
          return { ...prev, y: newY };
        });
      }

      if (rampageBubble) {
        setRampageBubble(prev => {
          if (!prev) return null;
          const newY = prev.y + 0.5;
          if (newY > 105) return null;
          return { ...prev, y: newY };
        });
      }

      if (wrapBubble) {
        setWrapBubble(prev => {
          if (!prev) return null;
          const newY = prev.y + 0.5;
          if (newY > 105) return null;
          return { ...prev, y: newY };
        });
      }
    }, 16);
    
    return () => clearInterval(loop);
  }, [screen, paused, theme, createParticles, playEnemyDestroySound, playAbilityCollectSound, updateScore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to trigger revive modal
  const triggerReviveModal = useCallback(() => {
    setPaused(true);
    setShowReviveModal(true);
    // Pause BGM when modal shows
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  useEffect(() => {
    if (screen !== 'game' || paused || gameOver) return;

    const collisionCheck = setInterval(() => {
      const planeX = planeXRef.current;
      const planeY = 85;

      // Powerup pickups with plane
      if (rampageBubble) {
        if (Math.abs(planeX - rampageBubble.x) < 8 && Math.abs(planeY - rampageBubble.y) < 8) {
          createParticles(rampageBubble.x, rampageBubble.y, theme.rampage, 12);
          playAbilityCollectSound();  // Sound effect
          setRampageBubble(null);
          setRampageActive(true);
          setRampageTimer(5000);
        }
      }

      if (wrapBubble) {
        if (Math.abs(planeX - wrapBubble.x) < 8 && Math.abs(planeY - wrapBubble.y) < 8) {
          createParticles(wrapBubble.x, wrapBubble.y, theme.wrap, 12);
          playAbilityCollectSound();  // Sound effect
          setWrapBubble(null);
          setWrapActive(true);
        }
      }

      // Plane collision with enemies
      setEnemies(currentEnemies => {
        const hitEnemy = currentEnemies.some(e => Math.abs(planeX - e.x) < 8 && Math.abs(planeY - e.y) < 8);
        if (hitEnemy) {
          // Player is immune during boss warning
          if (bossWarning) {
            return currentEnemies.filter(e => !(Math.abs(planeX - e.x) < 8 && Math.abs(planeY - e.y) < 8));
          } else if (wrapActive) {
            setWrapActive(false);
            createParticles(planeX, planeY, theme.wrap, 12);
            return currentEnemies.filter(e => !(Math.abs(planeX - e.x) < 8 && Math.abs(planeY - e.y) < 8));
          } else {
            // Player takes damage
            setPlayerHealth(h => {
              const newHealth = h - 1;
              if (newHealth <= 0) {
                triggerReviveModal();
              }
              return Math.max(0, newHealth);
            });
            playPlayerHitSound();  // Sound effect
            createParticles(planeX, planeY, theme.plane, 8);
            return currentEnemies.filter(e => !(Math.abs(planeX - e.x) < 8 && Math.abs(planeY - e.y) < 8));
          }
        }
        return currentEnemies;
      });

      setAliens(currentAliens => {
        const hitAlien = currentAliens.some(a => Math.abs(planeX - a.x) < 8 && Math.abs(planeY - a.y) < 8);
        if (hitAlien) {
          // Player is immune during boss warning
          if (bossWarning) {
            return currentAliens.filter(a => !(Math.abs(planeX - a.x) < 8 && Math.abs(planeY - a.y) < 8));
          } else if (wrapActive) {
            setWrapActive(false);
            createParticles(planeX, planeY, theme.wrap, 12);
            return currentAliens.filter(a => !(Math.abs(planeX - a.x) < 8 && Math.abs(planeY - a.y) < 8));
          } else {
            // Player takes damage
            setPlayerHealth(h => {
              const newHealth = h - 1;
              if (newHealth <= 0) {
                triggerReviveModal();
              }
              return Math.max(0, newHealth);
            });
            playPlayerHitSound();  // Sound effect
            createParticles(planeX, planeY, theme.plane, 8);
            return currentAliens.filter(a => !(Math.abs(planeX - a.x) < 8 && Math.abs(planeY - a.y) < 8));
          }
        }
        return currentAliens;
      });

      setAsteroids(currentAsteroids => {
        const hitAsteroid = currentAsteroids.some(ast => Math.abs(planeX - ast.x) < 12 && Math.abs(planeY - ast.y) < 12);
        if (hitAsteroid) {
          // Player is immune during boss warning
          if (bossWarning) {
            return currentAsteroids.filter(ast => !(Math.abs(planeX - ast.x) < 12 && Math.abs(planeY - ast.y) < 12));
          } else if (wrapActive) {
            setWrapActive(false);
            createParticles(planeX, planeY, theme.wrap, 12);
            return currentAsteroids.filter(ast => !(Math.abs(planeX - ast.x) < 12 && Math.abs(planeY - ast.y) < 12));
          } else {
            // Player takes damage
            setPlayerHealth(h => {
              const newHealth = h - 1;
              if (newHealth <= 0) {
                triggerReviveModal();
              }
              return Math.max(0, newHealth);
            });
            playPlayerHitSound();  // Sound effect
            createParticles(planeX, planeY, theme.plane, 8);
            return currentAsteroids.filter(ast => !(Math.abs(planeX - ast.x) < 12 && Math.abs(planeY - ast.y) < 12));
          }
        }
        return currentAsteroids;
      });

      setEnemyBullets(currentEnemyBullets => {
        const hitBullet = currentEnemyBullets.some(eb => Math.abs(planeX - eb.x) < 6 && Math.abs(planeY - eb.y) < 6);
        if (hitBullet) {
          // Player is immune during boss warning
          if (bossWarning) {
            return currentEnemyBullets.filter(eb => !(Math.abs(planeX - eb.x) < 6 && Math.abs(planeY - eb.y) < 6));
          } else if (wrapActive) {
            setWrapActive(false);
            createParticles(planeX, planeY, theme.wrap, 12);
            return currentEnemyBullets.filter(eb => !(Math.abs(planeX - eb.x) < 6 && Math.abs(planeY - eb.y) < 6));
          } else {
            // Player takes damage
            setPlayerHealth(h => {
              const newHealth = h - 1;
              if (newHealth <= 0) {
                triggerReviveModal();
              }
              return Math.max(0, newHealth);
            });
            playPlayerHitSound();  // Sound effect
            createParticles(planeX, planeY, theme.plane, 8);
            return currentEnemyBullets.filter(eb => !(Math.abs(planeX - eb.x) < 6 && Math.abs(planeY - eb.y) < 6));
          }
        }
        return currentEnemyBullets;
      });
    }, 16);

    return () => clearInterval(collisionCheck);
  }, [screen, paused, gameOver, theme, createParticles, playAbilityCollectSound, playPlayerHitSound, triggerReviveModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (gameOver) {
      if (score > bestScore) {
        setBestScore(score);
      }
      if (kills > bestKills) {
        setBestKills(kills);
      }
      // Save stats using GamePix.localStorage (with safety check)
      if (typeof window !== 'undefined' && window.GamePix && window.GamePix.localStorage) {
        try {
          window.GamePix.localStorage.setItem('spacelaxer-stats', JSON.stringify({
            bestScore: Math.max(score, bestScore),
            bestKills: Math.max(kills, bestKills)
          }));
        } catch (error) {
          console.error('Failed to save stats:', error);
        }
      }
    }
  }, [gameOver, score, kills, bestScore, bestKills]);

  const handlePlaneMove = useCallback((clientX) => {
    if (!touchStartX.current || !gameAreaRef.current) return;
    
    const rect = gameAreaRef.current.getBoundingClientRect();
    const movePercent = ((clientX - touchStartX.current) / rect.width) * 100;
    const newX = Math.max(5, Math.min(90, planeXRef.current + movePercent));
    
    planeXRef.current = newX;
    touchStartX.current = clientX;
    
    const planeEl = document.getElementById('player-plane');
    if (planeEl) {
      planeEl.style.left = `${newX}%`;
    }
  }, []);

  const handleTouchStart = (e) => {
    if (screen !== 'game' || paused || gameOver) return;
    e.preventDefault();
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (screen !== 'game' || paused || gameOver) return;
    e.preventDefault();
    handlePlaneMove(e.touches[0].clientX);
  };

  const handleButtonClick = (action) => {
    action();
  };

  // GamePix SDK - Rewarded Ad (EXACT pattern from docs for toolkit detection)
  const assignReward = () => {
    setShowReviveModal(false);
    setPlayerHealth(2);
    setPaused(false);
    setAdLoading(false);
    if (audioRef.current) audioRef.current.play().catch(() => {});
  };

  const denyReward = () => {
    setAdLoading(false);
    if (audioRef.current) audioRef.current.play().catch(() => {});
  };

  const handleWatchAd = () => {
    if (audioRef.current) audioRef.current.pause();
    setAdLoading(true);

    // Exact pattern from GamePix documentation - NO GUARDS
    // SDK will throw its own errors: GAMEPIX_LOADED_NOT_CALLED, REWARD_AD_CALLED_TWICE
    GamePix.rewardAd().then(function(res) {
      if (res.success) {
        assignReward();
      } else {
        denyReward();
      }
    }).catch(function(error) {
      // Handle SDK errors
      console.error('GamePix rewardAd error:', error);
      denyReward();
    });
  };

  // Retry without watching ad - full reset
  const handleRetry = () => {
    setShowReviveModal(false);
    startGame(); // This will reset everything
  };

  const startGame = () => {
    setShowBlastAnimation(true);
    setTimeout(() => {
      setScreen('game');
      setPaused(false);
      setGameOver(false);
      updateScore(0);
      setKills(0);
      setDistance(0);
      setGameSpeed(0.8);
      setBulletSpeed(2);
      setBullets([]);
      setEnemyBullets([]);
      setEnemies([]);
      setAliens([]);
      setAsteroids([]);
      setStars([]);
      setBgElements([]);
      setParticles([]);
      planeXRef.current = 50;
      laxifierHitsRef.current = 0;
      setLaxifierBubble(null);
      setLaxifierActive(false);
      setLaxifierTimer(0);
      setRampageBubble(null);
      setRampageActive(false);
      setRampageTimer(0);
      setWrapBubble(null);
      setWrapActive(false);
      setScrollOffset(0);
      setShowBlastAnimation(false);
      // Reset new features
      setPlayerHealth(3);
      setBoss(null);
      setBossWarning(false);
      setLastBossScore(0);  // Updated from lastBossSpeed
      setBossCount(0);  // Reset boss count
      setBaseFiringSpeed(1.0);  // Reset firing speed
      setWaveActive(false);
      setLastWaveTime(0);
      
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
    }, 800);
  };

  const togglePause = () => {
    if (paused) {
      setShowBlastAnimation(true);
      setTimeout(() => {
        setPaused(false);
        setShowBlastAnimation(false);
        if (audioRef.current) {
          audioRef.current.play().catch(e => console.log('Audio play failed:', e));
        }
      }, 600);
    } else {
      setPaused(true);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
  };

  const goToMenu = () => {
    setScreen('menu');
    setPaused(false);
    setGameOver(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  if (screen === 'menu') {
    return (
      <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'fixed', top: 0, left: 0 }} className={`bg-gradient-to-b ${theme.bgGradient}`}>
      <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }} className={`bg-gradient-to-b ${theme.bgGradient} flex flex-col items-center justify-center`}>
          
          {/* Animated asteroids background */}
          {introAsteroids.map(ast => (
            <div 
              key={ast.id} 
              className="absolute" 
              style={{ 
                left: `${ast.x}%`, 
                top: `${ast.y}%`, 
                transform: `translateX(-50%) rotate(${ast.rotation}deg)`,
                opacity: 0.4,
                zIndex: 1
              }}
            >
              <svg width={ast.size} height={ast.size} viewBox="0 0 100 100">
                <defs>
                  <radialGradient id={`menuAstGrad-${ast.id}`}>
                    <stop offset="0%" style={{ stopColor: '#ff8c00', stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: '#ff4500', stopOpacity: 0.8 }} />
                    <stop offset="100%" style={{ stopColor: '#8b0000', stopOpacity: 0.5 }} />
                  </radialGradient>
                </defs>
                <circle cx="50" cy="50" r="45" fill="none" stroke={theme.asteroid} strokeWidth="10" opacity="0.6" style={{ filter: `blur(10px)` }}/>
                <circle cx="50" cy="50" r="30" fill={`url(#menuAstGrad-${ast.id})`}/>
                <circle cx="45" cy="45" r="3" fill="#3e2723" opacity="0.7"/>
                <circle cx="58" cy="48" r="4" fill="#3e2723" opacity="0.7"/>
                <circle cx="50" cy="55" r="3" fill="#3e2723" opacity="0.7"/>
              </svg>
            </div>
          ))}
          
          {showBlastAnimation && (
            <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
              <div className="relative w-full h-full">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute top-1/2 left-1/2 rounded-full border-4"
                    style={{
                      width: '50px',
                      height: '50px',
                      borderColor: blueTheme ? '#38bdf8' : '#fbbf24',
                      transform: 'translate(-50%, -50%)',
                      animation: `blast-expand 0.8s ease-out ${i * 0.1}s forwards`,
                      opacity: 0
                    }}
                  />
                ))}
                {[...Array(12)].map((_, i) => {
                  const angle = (i * 30) * (Math.PI / 180);
                  return (
                    <div
                      key={`particle-${i}`}
                      className="absolute top-1/2 left-1/2 rounded-full"
                      style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: blueTheme ? '#38bdf8' : '#fbbf24',
                        boxShadow: `0 0 15px ${blueTheme ? '#38bdf8' : '#fbbf24'}`,
                        transform: 'translate(-50%, -50%)',
                        animation: 'blast-particle 0.8s ease-out forwards',
                        ['--angle-x']: Math.cos(angle),
                        ['--angle-y']: Math.sin(angle)
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}
          
          <div 
            className="absolute pointer-events-none"
            style={{ 
              left: `${menuPlanePos.x}%`, 
              top: `${menuPlanePos.y}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            }}
          >
            <svg width="40" height="40" viewBox="0 0 100 100" className="drop-shadow-2xl" style={{ filter: `drop-shadow(0 0 15px ${theme.plane})` }}>
              <path d="M50 10 L45 25 L35 50 L45 48 L50 45 L55 48 L65 50 L55 25 Z" fill={theme.plane} opacity="1"/>
              <polygon points="35,50 20,55 35,58" fill={theme.planeDark} opacity="1"/>
              <polygon points="65,50 80,55 65,58" fill={theme.planeDark} opacity="1"/>
              <path d="M45 48 L48 75 L50 78 L52 75 L55 48 Z" fill={theme.planeDark} opacity="1"/>
            </svg>
          </div>
          
          <h1 
            className={`text-6xl sm:text-7xl md:text-8xl font-black ${theme.text} mb-1 tracking-widest drop-shadow-2xl text-center relative`} 
            style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '0.15em', textShadow: '0 0 20px rgba(56, 189, 248, 0.5)', zIndex: 10 }}
          >
            SPACE
          </h1>
          <h1 
            className={`text-6xl sm:text-7xl md:text-8xl font-black ${theme.text} mb-8 sm:mb-12 tracking-widest drop-shadow-2xl text-center relative`} 
            style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '0.15em', textShadow: '0 0 20px rgba(56, 189, 248, 0.5)', zIndex: 10 }}
          >
            LAXER
          </h1>

          <div className={`${theme.text} text-center mb-8 space-y-1 relative`} style={{ zIndex: 10 }}>
            <p className="text-lg font-bold">Best Score: {bestScore}</p>
            <p className="text-lg font-bold">Best Kills: {bestKills}</p>
          </div>
          
          <button
            onTouchEnd={(e) => { e.preventDefault(); handleButtonClick(startGame); }}
            onClick={(e) => { e.preventDefault(); handleButtonClick(startGame); }}
            className={`${theme.button} font-bold text-2xl sm:text-3xl px-12 sm:px-16 py-5 sm:py-6 rounded-lg mb-3 sm:mb-4 transition-all duration-300 active:scale-95 touch-manipulation select-none w-64 sm:w-72 shadow-2xl relative`}
            style={{ zIndex: 10 }}
          >
            START
          </button>
          <button
            onTouchEnd={(e) => { e.preventDefault(); handleButtonClick(() => setScreen('settings')); }}
            onClick={(e) => { e.preventDefault(); handleButtonClick(() => setScreen('settings')); }}
            className={`${theme.buttonSecondary} font-bold text-xl sm:text-2xl px-12 sm:px-16 py-4 rounded-lg transition-all duration-300 active:scale-95 touch-manipulation select-none w-64 sm:w-72 shadow-xl relative`}
            style={{ zIndex: 10 }}
          >
            SETTINGS
          </button>
        </div>
        
        <style>{`
          @keyframes blast-expand {
            0% {
              width: 50px;
              height: 50px;
              opacity: 1;
            }
            100% {
              width: 800px;
              height: 800px;
              opacity: 0;
            }
          }
          
          @keyframes blast-particle {
            0% {
              transform: translate(-50%, -50%) translate(0, 0);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -50%) translate(calc(var(--angle-x) * 300px), calc(var(--angle-y) * 300px));
              opacity: 0;
            }
          }
        `}</style>
      </div>
    );
  }

  if (screen === 'settings') {
    return (
      <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'fixed', top: 0, left: 0 }} className={theme.bgSolid}>
        <div style={{ width: '100%', height: '100%', overflow: 'auto' }} className={`${theme.bgSolid} flex flex-col items-center justify-center p-6`}>
          <h2 className={`text-4xl sm:text-5xl font-bold ${theme.text} mb-8 sm:mb-12`}>SETTINGS</h2>
          
          <div className="w-full max-w-sm space-y-6 sm:space-y-8 mb-8">
            {/* Blue Theme Toggle */}
            <div className="flex items-center justify-between">
              <span className={`${theme.text} text-xl sm:text-2xl`}>Blue Theme</span>
              <button
                onTouchEnd={(e) => { e.preventDefault(); setBlueTheme(!blueTheme); }}
                onClick={(e) => { e.preventDefault(); setBlueTheme(!blueTheme); }}
                className={`w-20 h-10 rounded-full transition touch-manipulation shadow-lg ${blueTheme ? 'bg-cyan-500' : 'bg-gray-500'}`}
              >
                <div className={`w-8 h-8 bg-white rounded-full transform transition ${blueTheme ? 'translate-x-11' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Music Volume Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`${theme.text} text-xl sm:text-2xl`}>Music</span>
                <span className={`${theme.text} text-lg`}>{Math.round(musicVolume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(musicVolume * 100)}
                onChange={(e) => setMusicVolume(parseInt(e.target.value) / 100)}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, ${blueTheme ? '#38bdf8' : '#fbbf24'} 0%, ${blueTheme ? '#38bdf8' : '#fbbf24'} ${musicVolume * 100}%, #374151 ${musicVolume * 100}%, #374151 100%)`
                }}
              />
            </div>

            {/* FX Volume Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`${theme.text} text-xl sm:text-2xl`}>Sound FX</span>
                <span className={`${theme.text} text-lg`}>{Math.round(fxVolume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(fxVolume * 100)}
                onChange={(e) => setFxVolume(parseInt(e.target.value) / 100)}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, ${blueTheme ? '#38bdf8' : '#fbbf24'} 0%, ${blueTheme ? '#38bdf8' : '#fbbf24'} ${fxVolume * 100}%, #374151 ${fxVolume * 100}%, #374151 100%)`
                }}
              />
            </div>
          </div>

          <div className={`${theme.text} text-base sm:text-lg mb-8 text-center space-y-2`}>
            <p>• Always firing while moving</p>
            <p>• Rampage + Laxifier: 5-way spread</p>
            <p>• Wrap: Protection from 1 hit</p>
            <p>• Enemies shoot back!</p>
          </div>
          
          <button
            onTouchEnd={(e) => { e.preventDefault(); handleButtonClick(() => setScreen('menu')); }}
            onClick={(e) => { e.preventDefault(); handleButtonClick(() => setScreen('menu')); }}
            className={`${theme.button} font-bold text-xl sm:text-2xl px-12 sm:px-16 py-4 rounded-lg transition-all duration-300 active:scale-95 touch-manipulation select-none shadow-xl`}
          >
            BACK
          </button>
        </div>
      </div>
    );
  }

  const currentTime = Date.now();

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'fixed', top: 0, left: 0 }} className={theme.bgSolid}>
      <div 
        ref={gameAreaRef}
        style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', touchAction: 'none' }}
        className={theme.bgSolid}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => touchStartX.current = null}
        onMouseDown={(e) => { touchStartX.current = e.clientX; }}
        onMouseMove={(e) => {
          if (!touchStartX.current || paused || gameOver) return;
          handlePlaneMove(e.clientX);
        }}
        onMouseUp={() => touchStartX.current = null}
      >
        {bgElements.map(el => (
          <div key={el.id} className="absolute" style={{ left: `${el.x}%`, top: `${el.y}%`, transform: `translateX(-50%) rotate(${el.rotation}deg)` }}>
            {el.type === 'planet' && (
              <div className="relative">
                <div 
                  className="rounded-full opacity-60"
                  style={{ 
                    width: `${el.size}px`, 
                    height: `${el.size}px`, 
                    backgroundColor: el.color,
                    boxShadow: `0 0 ${el.size}px ${el.color}`
                  }}
                />
                {el.hasRings && (
                  <div 
                    className="absolute rounded-full opacity-40"
                    style={{
                      width: `${el.size * 1.8}px`,
                      height: `${el.size * 0.3}px`,
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      border: `3px solid ${el.color}`,
                      boxShadow: `0 0 15px ${el.color}`
                    }}
                  />
                )}
              </div>
            )}
            {el.type === 'galaxy' && (
              <div className="relative opacity-40" style={{ width: `${el.size}px`, height: `${el.size}px` }}>
                <div className="absolute inset-0 rounded-full" style={{ 
                  background: `radial-gradient(circle, ${blueTheme ? '#c084fc' : '#fbbf24'} 0%, transparent 70%)`
                }} />
              </div>
            )}
            {el.type === 'shootingStar' && (
              <div className="relative" style={{ width: '30px', height: '4px' }}>
                <div className="absolute w-full h-full rounded-full" style={{
                  background: `linear-gradient(90deg, ${theme.star} 0%, transparent 100%)`,
                  boxShadow: `0 0 10px ${theme.star}`
                }} />
              </div>
            )}
          </div>
        ))}

        {stars.map(star => (
          <div
            key={star.id}
            className="absolute rounded-full animate-pulse"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: theme.star,
              boxShadow: `0 0 ${star.size * 2}px ${theme.star}`
            }}
          />
        ))}

        {particles.map(p => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: '4px',
              height: '4px',
              backgroundColor: p.color,
              opacity: p.life / 30,  // Updated to match particle lifetime of 30 frames
              boxShadow: `0 0 6px ${p.color}`
            }}
          />
        ))}

        <button
          onClick={togglePause}
          className={`absolute top-3 right-3 ${theme.bgSolid} bg-opacity-90 ${theme.text} font-bold px-3 py-2 rounded-full ${theme.border} border-2 shadow-lg z-20 hover:scale-105 transition-transform`}
        >
          {paused ? '▶' : '⏸'}
        </button>

        <div className={`absolute top-3 sm:top-4 left-3 sm:left-4 ${theme.bgSolid} bg-opacity-90 ${theme.text} font-bold px-3 sm:px-4 py-2 rounded ${theme.border} border-2 text-xs sm:text-base shadow-lg`}>
          <div className="text-xs">SCORE</div>
          <div className="text-xl sm:text-2xl">{score}</div>
          <div className="text-xs mt-1">KILLS: {kills}</div>
        </div>

        {/* Player Lives Display */}
        <div className={`absolute bottom-3 sm:bottom-4 left-3 sm:left-4 ${theme.bgSolid} bg-opacity-90 ${theme.text} font-bold px-3 py-2 rounded ${theme.border} border-2 shadow-lg flex gap-1`}>
          {[...Array(3)].map((_, i) => (
            <svg key={i} width="20" height="20" viewBox="0 0 100 100" opacity={i < playerHealth ? 1 : 0.3}>
              <path d="M50 10 L45 25 L35 50 L45 48 L50 45 L55 48 L65 50 L55 25 Z" fill={theme.plane}/>
              <polygon points="35,50 20,55 35,58" fill={theme.planeDark}/>
              <polygon points="65,50 80,55 65,58" fill={theme.planeDark}/>
            </svg>
          ))}
        </div>

        {/* Boss Warning */}
        {bossWarning && (
          <div className="absolute inset-0 bg-red-900 bg-opacity-80 flex flex-col items-center justify-center z-40 animate-pulse">
            <h2 className="text-6xl font-bold text-red-500 mb-4 drop-shadow-2xl">⚠️ WARNING ⚠️</h2>
            <p className="text-3xl text-white font-bold drop-shadow-xl">BOSS INCOMING!</p>
          </div>
        )}

        <div className={`absolute bottom-3 sm:bottom-4 right-3 sm:right-4 ${theme.bgSolid} bg-opacity-90 ${theme.text} font-bold px-3 sm:px-4 py-2 rounded ${theme.border} border-2 text-xs sm:text-base shadow-lg`}>
          <div className="text-xs">SPEED</div>
          <div className="text-xl sm:text-2xl">{gameSpeed.toFixed(1)}x</div>
        </div>

        <div className="absolute top-32 sm:top-36 left-1/2 transform -translate-x-1/2 flex gap-2 flex-wrap justify-center max-w-sm">
          {laxifierActive && (
            <div 
              className="font-bold px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm shadow-2xl"
              style={{
                backgroundColor: theme.laxifier,
                color: '#000',
                boxShadow: `0 0 20px ${theme.laxifier}`
              }}
            >
              ⚡ LAXIFIER: {(laxifierTimer / 1000).toFixed(1)}s
            </div>
          )}
          {rampageActive && (
            <div 
              className="font-bold px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm shadow-2xl"
              style={{
                backgroundColor: theme.rampage,
                color: '#fff',
                boxShadow: `0 0 20px ${theme.rampage}`
              }}
            >
              🔥 RAMPAGE: {(rampageTimer / 1000).toFixed(1)}s
            </div>
          )}
          {wrapActive && (
            <div 
              className="font-bold px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm shadow-2xl animate-pulse"
              style={{
                backgroundColor: theme.wrap,
                color: '#000',
                boxShadow: `0 0 20px ${theme.wrap}`
              }}
            >
              🛡️ WRAP ACTIVE
            </div>
          )}
        </div>

        {paused && (
          <div className="absolute inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center z-30">
            <h2 className="text-5xl font-bold text-white mb-12">PAUSED</h2>
            <button
              onClick={togglePause}
              className={`${theme.button} font-bold text-2xl px-16 py-6 rounded-lg mb-4 shadow-2xl hover:scale-105 transition-transform`}
            >
              RESUME
            </button>
            <button
              onClick={goToMenu}
              className={`${theme.buttonSecondary} font-bold text-2xl px-16 py-6 rounded-lg shadow-xl hover:scale-105 transition-transform`}
            >
              MENU
            </button>
          </div>
        )}

        {/* Revive Modal - GamePix Rewarded Ad */}}
        {showReviveModal && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-50 bg-black bg-opacity-90">
            <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-8 rounded-2xl shadow-2xl border-4 border-yellow-500 max-w-md mx-4">
              <h2 className="text-4xl font-bold text-yellow-400 mb-4 text-center animate-pulse">
                💀 OUT OF LIVES!
              </h2>
              <div className="text-white text-center mb-6 space-y-2">
                <p className="text-2xl font-bold">Score: {score}</p>
                <p className="text-xl">Kills: {kills}</p>
              </div>
              
              <div className="space-y-4">
                {/* Watch Ad Button */}
                <button
                  onClick={handleWatchAd}
                  disabled={adLoading}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-xl px-8 py-4 rounded-lg shadow-xl hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {adLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      Loading Ad...
                    </>
                  ) : (
                    <>
                      <span className="text-2xl">📺</span>
                      Watch Ad for 2 Lives
                    </>
                  )}
                </button>

                <div className="text-center text-gray-400 text-sm">
                  Watch a short video to continue with 2 extra lives
                </div>

                {/* Retry Button */}
                <button
                  onClick={handleRetry}
                  disabled={adLoading}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-lg px-8 py-3 rounded-lg shadow-xl hover:scale-105 transition-transform disabled:opacity-50"
                >
                  Retry from Start
                </button>
              </div>

              <div className="mt-4 text-center text-gray-500 text-xs">
                Continue playing or start fresh
              </div>
            </div>
          </div>
        )}

        {gameOver && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-40 bg-black bg-opacity-95">
            {/* Scan line effect */}
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 1px, transparent 1px, transparent 2px)',
              zIndex: 1
            }} />
            
            {/* Content */}
            <div className="relative z-10 flex flex-col items-center">
              {/* GAME OVER text - pixel style */}
              <h2 
                className="text-7xl font-bold mb-12 tracking-wider"
                style={{
                  fontFamily: 'monospace',
                  color: '#f5f5dc',
                  textShadow: '4px 4px 0px #000000, 8px 8px 20px rgba(245, 245, 220, 0.3)',
                  letterSpacing: '0.2em'
                }}
              >
                GAME OVER
              </h2>

              {/* Stats */}
              <div className="mb-12 text-center space-y-3">
                <p 
                  className="text-2xl font-bold"
                  style={{
                    fontFamily: 'monospace',
                    color: '#ffd700',
                    textShadow: '2px 2px 0px #000000'
                  }}
                >
                  SCORE: {score}
                </p>
                <p 
                  className="text-xl"
                  style={{
                    fontFamily: 'monospace',
                    color: '#c0c0c0',
                    textShadow: '2px 2px 0px #000000'
                  }}
                >
                  KILLS: {kills}
                </p>
                <p 
                  className="text-lg"
                  style={{
                    fontFamily: 'monospace',
                    color: '#808080',
                    textShadow: '1px 1px 0px #000000'
                  }}
                >
                  BEST: {bestScore}
                </p>
              </div>

              {/* PLAY AGAIN text */}
              <p 
                className="text-3xl font-bold mb-8"
                style={{
                  fontFamily: 'monospace',
                  color: '#f5f5dc',
                  textShadow: '2px 2px 0px #000000',
                  letterSpacing: '0.15em'
                }}
              >
                PLAY AGAIN?
              </p>

              {/* YES / NO Selection */}
              <div className="flex items-center gap-16">
                {/* YES Option */}
                <button
                  onClick={() => {
                    setGameOverSelection('retry');
                    startGame();
                  }}
                  onMouseEnter={() => setGameOverSelection('retry')}
                  className="flex items-center gap-4 transition-all"
                  style={{ outline: 'none' }}
                >
                  <span 
                    className="text-4xl font-bold"
                    style={{
                      fontFamily: 'monospace',
                      color: gameOverSelection === 'retry' ? '#00ff00' : '#f5f5dc',
                      textShadow: gameOverSelection === 'retry' 
                        ? '0 0 10px #00ff00, 3px 3px 0px #000000' 
                        : '2px 2px 0px #000000',
                      transition: 'all 0.2s'
                    }}
                  >
                    {gameOverSelection === 'retry' && '► '}YES
                  </span>
                </button>

                {/* NO Option */}
                <button
                  onClick={() => {
                    setGameOverSelection('menu');
                    goToMenu();
                  }}
                  onMouseEnter={() => setGameOverSelection('menu')}
                  className="flex items-center gap-4 transition-all"
                  style={{ outline: 'none' }}
                >
                  <span 
                    className="text-4xl font-bold"
                    style={{
                      fontFamily: 'monospace',
                      color: gameOverSelection === 'menu' ? '#ff0000' : '#f5f5dc',
                      textShadow: gameOverSelection === 'menu' 
                        ? '0 0 10px #ff0000, 3px 3px 0px #000000' 
                        : '2px 2px 0px #000000',
                      transition: 'all 0.2s'
                    }}
                  >
                    {gameOverSelection === 'menu' && '► '}NO
                  </span>
                </button>
              </div>

              {/* Hint text */}
              <p 
                className="text-sm mt-12 opacity-60"
                style={{
                  fontFamily: 'monospace',
                  color: '#808080',
                  letterSpacing: '0.1em'
                }}
              >
                HOVER TO SELECT
              </p>
            </div>
          </div>
        )}

        {showBlastAnimation && (
          <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div className="relative w-full h-full">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 rounded-full border-4"
                  style={{
                    width: '50px',
                    height: '50px',
                    borderColor: blueTheme ? '#38bdf8' : '#fbbf24',
                    transform: 'translate(-50%, -50%)',
                    animation: `blast-expand 0.8s ease-out ${i * 0.1}s forwards`,
                    opacity: 0
                  }}
                />
              ))}
              {[...Array(12)].map((_, i) => {
                const angle = (i * 30) * (Math.PI / 180);
                return (
                  <div
                    key={`particle-${i}`}
                    className="absolute top-1/2 left-1/2 rounded-full"
                    style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: blueTheme ? '#38bdf8' : '#fbbf24',
                      boxShadow: `0 0 15px ${blueTheme ? '#38bdf8' : '#fbbf24'}`,
                      transform: 'translate(-50%, -50%)',
                      animation: 'blast-particle 0.8s ease-out forwards',
                      ['--angle-x']: Math.cos(angle),
                      ['--angle-y']: Math.sin(angle)
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        <div id="player-plane" className="absolute" style={{ left: '50%', bottom: '10%', transform: 'translateX(-50%)' }}>
          {wrapActive && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <svg width={PLANE_SIZE + 30} height={PLANE_SIZE + 30} viewBox="0 0 100 100" className="wrap-bubble-container">
                <defs>
                  <radialGradient id="wrapGradient">
                    <stop offset="0%" stopColor="#00ffff" stopOpacity="0.4"/>
                    <stop offset="70%" stopColor="#00ffff" stopOpacity="0.2"/>
                    <stop offset="100%" stopColor="#00ffff" stopOpacity="0"/>
                  </radialGradient>
                </defs>
                {/* Outer glow layer */}
                <circle cx="50" cy="50" r="45" fill="url(#wrapGradient)" className="wrap-bubble-outer"/>
                {/* Main protective ring */}
                <circle cx="50" cy="50" r="40" fill="none" stroke="#00ffff" strokeWidth="3" opacity="0.8" className="wrap-bubble-ring"/>
                {/* Inner shimmer ring */}
                <circle cx="50" cy="50" r="35" fill="none" stroke="#ffffff" strokeWidth="2" opacity="0.6" className="wrap-bubble-inner"/>
                {/* Core fill */}
                <circle cx="50" cy="50" r="32" fill="#00ffff" opacity="0.15"/>
              </svg>
            </div>
          )}
          <svg width={PLANE_SIZE} height={PLANE_SIZE} viewBox="0 0 100 100" className="drop-shadow-2xl" style={{ filter: `drop-shadow(0 0 10px ${theme.plane})` }}>
            <path d="M50 10 L45 25 L35 50 L45 48 L50 45 L55 48 L65 50 L55 25 Z" fill={theme.plane}/>
            <polygon points="35,50 20,55 35,58" fill={theme.planeDark}/>
            <polygon points="65,50 80,55 65,58" fill={theme.planeDark}/>
            <path d="M45 48 L48 75 L50 78 L52 75 L55 48 Z" fill={theme.planeDark}/>
            <circle cx="50" cy="35" r="4" fill={blueTheme ? '#38bdf8' : '#fbbf24'}/>
          </svg>
        </div>

        {laxifierActive && (
          <>
            <div className="absolute" style={{ left: `${planeXRef.current - 4}%`, bottom: '10%', transform: 'translateX(-50%)' }}>
              <svg width={PLANE_SIZE} height={PLANE_SIZE} viewBox="0 0 100 100" className="drop-shadow-2xl opacity-90" style={{ filter: `drop-shadow(0 0 10px ${theme.plane})` }}>
                <path d="M50 10 L45 25 L35 50 L45 48 L50 45 L55 48 L65 50 L55 25 Z" fill={theme.plane}/>
                <polygon points="35,50 20,55 35,58" fill={theme.planeDark}/>
                <polygon points="65,50 80,55 65,58" fill={theme.planeDark}/>
                <path d="M45 48 L48 75 L50 78 L52 75 L55 48 Z" fill={theme.planeDark}/>
              </svg>
            </div>
            <div className="absolute" style={{ left: `${planeXRef.current + 4}%`, bottom: '10%', transform: 'translateX(-50%)' }}>
              <svg width={PLANE_SIZE} height={PLANE_SIZE} viewBox="0 0 100 100" className="drop-shadow-2xl opacity-90" style={{ filter: `drop-shadow(0 0 10px ${theme.plane})` }}>
                <path d="M50 10 L45 25 L35 50 L45 48 L50 45 L55 48 L65 50 L55 25 Z" fill={theme.plane}/>
                <polygon points="35,50 20,55 35,58" fill={theme.planeDark}/>
                <polygon points="65,50 80,55 65,58" fill={theme.planeDark}/>
                <path d="M45 48 L48 75 L50 78 L52 75 L55 48 Z" fill={theme.planeDark}/>
              </svg>
            </div>
          </>
        )}

        {bullets.map(b => (
          <div key={b.id} className="absolute" style={{ 
            left: `${b.x}%`, 
            top: `${b.y}%`, 
            transform: `translateX(-50%) rotate(${b.angle || 0}deg)`,
            transformOrigin: 'center center'
          }}>
            <svg width="10" height="24" viewBox="0 0 10 24">
              <defs>
                <linearGradient id="bulletGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: theme.bullet, stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: theme.bulletGlow, stopOpacity: 0.6 }} />
                </linearGradient>
              </defs>
              <rect x="3" y="0" width="4" height="24" fill="url(#bulletGrad)" rx="2"/>
              <ellipse cx="5" cy="2" rx="5" ry="4" fill={theme.bullet} opacity="0.8"/>
              <circle cx="5" cy="2" r="3" fill="#fff" opacity="0.8"/>
            </svg>
          </div>
        ))}

        {enemyBullets.map(eb => (
          <div key={eb.id} className="absolute" style={{ left: `${eb.x}%`, top: `${eb.y}%`, transform: 'translateX(-50%)' }}>
            <svg width="8" height="16" viewBox="0 0 8 16">
              <rect x="2" y="0" width="4" height="16" fill={theme.enemyDark} rx="2"/>
              <circle cx="4" cy="2" r="3" fill={theme.enemy} opacity="0.8"/>
            </svg>
          </div>
        ))}

        {enemies.map(e => {
          const size = 50 + ((e.maxHealth || 1) - 1) * 15; // Size increases with max health
          return (
            <div key={e.id} className="absolute" style={{ left: `${e.x}%`, top: `${e.y}%`, transform: 'translateX(-50%)' }}>
              {/* Health bar for multi-hit enemies */}
              {(e.maxHealth || 1) > 1 && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-12 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-red-500 transition-all duration-200"
                    style={{ width: `${((e.health || 1) / (e.maxHealth || 1)) * 100}%` }}
                  />
                </div>
              )}
              <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-2xl">
                <circle cx="50" cy="50" r="30" fill="none" stroke={theme.enemy} strokeWidth="4" opacity="0.6" style={{ filter: `blur(4px)` }}/>
                <path d="M50 85 L45 70 L35 45 L45 47 L50 50 L55 47 L65 45 L55 70 Z" fill={theme.enemy} stroke={theme.enemyDark} strokeWidth="2"/>
                <polygon points="35,45 20,40 35,37" fill={theme.enemyDark}/>
                <polygon points="65,45 80,40 65,37" fill={theme.enemyDark}/>
                <circle cx="50" cy="60" r="6" fill="#fff" opacity="0.9"/>
              </svg>
            </div>
          );
        })}

        {aliens.map(a => (
          <div key={a.id} className="absolute" style={{ left: `${a.x}%`, top: `${a.y}%`, transform: 'translateX(-50%)' }}>
            <svg width={ALIEN_SIZE} height={ALIEN_SIZE} viewBox="0 0 100 100" className="drop-shadow-2xl">
              <ellipse cx="50" cy="55" rx="40" ry="25" fill="none" stroke={theme.alien} strokeWidth="5" opacity="0.7" style={{ filter: `blur(5px)` }}/>
              <ellipse cx="50" cy="40" rx="18" ry="15" fill={theme.alien} opacity="0.8"/>
              <ellipse cx="50" cy="38" rx="18" ry="8" fill={theme.alienDark} opacity="0.6"/>
              <ellipse cx="50" cy="55" rx="35" ry="20" fill={theme.alienDark}/>
              <ellipse cx="50" cy="52" rx="35" ry="18" fill={theme.alien}/>
              <circle cx="30" cy="55" r="4" fill="#ffd700" opacity="1"/>
              <circle cx="50" cy="58" r="4" fill="#ffd700" opacity="1"/>
              <circle cx="70" cy="55" r="4" fill="#ffd700" opacity="1"/>
              <circle cx="30" cy="55" r="6" fill="#ffd700" opacity="0.3"/>
              <circle cx="50" cy="58" r="6" fill="#ffd700" opacity="0.3"/>
              <circle cx="70" cy="55" r="6" fill="#ffd700" opacity="0.3"/>
            </svg>
          </div>
        ))}

        {asteroids.map(ast => {
          const age = (currentTime - ast.spawnTime) / 1000;
          const size = ASTEROID_BASE_SIZE + age * 8;
          return (
            <div key={ast.id} className="absolute" style={{ left: `${ast.x}%`, top: `${ast.y}%`, transform: 'translateX(-50%)' }}>
              <svg width={size} height={size} viewBox="0 0 100 100" className="drop-shadow-2xl">
                <defs>
                  <radialGradient id={`astGrad-${ast.id}`}>
                    <stop offset="0%" style={{ stopColor: '#ff8c00', stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: '#ff4500', stopOpacity: 0.8 }} />
                    <stop offset="100%" style={{ stopColor: '#8b0000', stopOpacity: 0.5 }} />
                  </radialGradient>
                </defs>
                <circle cx="50" cy="50" r="45" fill="none" stroke={theme.asteroid} strokeWidth="10" opacity="0.6" style={{ filter: `blur(10px)` }}/>
                <ellipse cx="50" cy="90" rx="40" ry="30" fill="#ff6b00" opacity="0.7" style={{ filter: `blur(8px)` }}/>
                <ellipse cx="50" cy="85" rx="35" ry="25" fill="#ffa500" opacity="0.8" style={{ filter: `blur(6px)` }}/>
                <ellipse cx="50" cy="80" rx="30" ry="20" fill="#ffeb3b" opacity="0.9" style={{ filter: `blur(4px)` }}/>
                <circle cx="50" cy="50" r="30" fill={`url(#astGrad-${ast.id})`}/>
                <circle cx="45" cy="45" r="3" fill="#3e2723" opacity="0.7"/>
                <circle cx="58" cy="48" r="4" fill="#3e2723" opacity="0.7"/>
                <circle cx="50" cy="55" r="3" fill="#3e2723" opacity="0.7"/>
              </svg>
            </div>
          );
        })}

        {/* Boss */}
        {boss && (
          <div className="absolute" style={{ left: `${boss.x}%`, top: `${boss.y}%`, transform: 'translateX(-50%)' }}>
            {/* Boss Health Bar */}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-32 bg-gray-800 rounded-full overflow-hidden border-2"
                 style={{ borderColor: boss.type === 'laxitor' ? '#ffd700' : '#ff0000' }}>
              <div className="h-2 transition-all duration-300"
                   style={{ 
                     width: `${(boss.health / boss.maxHealth) * 100}%`,
                     background: boss.type === 'laxitor' 
                       ? 'linear-gradient(to right, #ffd700, #ffed4e)' 
                       : 'linear-gradient(to right, #ff0000, #ff6666)'
                   }}
              />
            </div>
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 font-bold whitespace-nowrap text-xs"
                 style={{ color: boss.type === 'laxitor' ? '#ffd700' : '#ffff00' }}>
              {boss.type === 'laxitor' ? '⚡ LAXITOR' : '🔥 RAMPAGANT'}: {boss.health}/{boss.maxHealth}
            </div>
            
            {/* Boss Jet Design - Size and color based on type */}
            <svg 
              width={boss.type === 'laxitor' ? '90' : '80'} 
              height={boss.type === 'laxitor' ? '90' : '80'} 
              viewBox="0 0 100 100" 
              className="drop-shadow-2xl animate-pulse"
            >
              <defs>
                {boss.type === 'laxitor' ? (
                  /* Laxitor - Gold/Yellow theme */
                  <>
                    <linearGradient id="bossGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#ffd700', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#ff8c00', stopOpacity: 0.9 }} />
                    </linearGradient>
                    <filter id="bossGlow">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </>
                ) : (
                  /* Rampagant - Red/Orange theme */
                  <>
                    <linearGradient id="bossGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#ff0000', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#8b0000', stopOpacity: 0.8 }} />
                    </linearGradient>
                    <filter id="bossGlow">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </>
                )}
              </defs>
              
              {/* Main body - angular jet design */}
              <polygon 
                points="50,15 45,30 40,50 45,52 50,55 55,52 60,50 55,30" 
                fill="url(#bossGrad)" 
                stroke={boss.type === 'laxitor' ? '#ffd700' : '#ff0000'} 
                strokeWidth="2"
              />
              
              {/* Wings - swept back design */}
              <polygon 
                points="40,50 15,60 20,65 40,58" 
                fill={boss.type === 'laxitor' ? '#ffd700' : '#ff0000'}
              />
              <polygon 
                points="60,50 85,60 80,65 60,58" 
                fill={boss.type === 'laxitor' ? '#ffd700' : '#ff0000'}
              />
              <polygon 
                points="40,50 10,55 15,58 40,53" 
                fill={boss.type === 'laxitor' ? '#ff8c00' : '#8b0000'}
              />
              <polygon 
                points="60,50 90,55 85,58 60,53" 
                fill={boss.type === 'laxitor' ? '#ff8c00' : '#8b0000'}
              />
              
              {/* Rear section */}
              <path 
                d="M45 52 L42 75 L45 80 L50 82 L55 80 L58 75 L55 52 Z" 
                fill={boss.type === 'laxitor' ? '#ff8c00' : '#8b0000'} 
                stroke={boss.type === 'laxitor' ? '#ffd700' : '#ff0000'} 
                strokeWidth="1.5"
              />
              
              {/* Engine glow */}
              <circle 
                cx="50" 
                cy="80" 
                r="6" 
                fill={boss.type === 'laxitor' ? '#ffed4e' : '#ff4400'} 
                opacity="0.9" 
                filter="url(#bossGlow)"
              >
                <animate attributeName="r" values="5;8;5" dur="0.5s" repeatCount="indefinite"/>
              </circle>
              <circle 
                cx="50" 
                cy="80" 
                r="4" 
                fill={boss.type === 'laxitor' ? '#ffff00' : '#ffaa00'} 
                opacity="1"
              >
                <animate attributeName="opacity" values="1;0.5;1" dur="0.5s" repeatCount="indefinite"/>
              </circle>
              
              {/* Cockpit */}
              <ellipse cx="50" cy="35" rx="8" ry="12" fill="#000" opacity="0.8"/>
              <ellipse 
                cx="50" 
                cy="33" 
                rx="6" 
                ry="8" 
                fill={boss.type === 'laxitor' ? '#ffd700' : '#00ffff'} 
                opacity="0.6"
              />
              
              {/* Warning stripes */}
              <rect 
                x="48" 
                y="55" 
                width="4" 
                height="8" 
                fill={boss.type === 'laxitor' ? '#ff8c00' : '#ffff00'} 
                opacity="0.8"
              />
            </svg>
          </div>
        )}

        {laxifierBubble && (
          <div className="absolute" style={{ left: `${laxifierBubble.x}%`, top: `${laxifierBubble.y}%`, transform: 'translateX(-50%)' }}>
            <svg width={BUBBLE_SIZE} height={BUBBLE_SIZE} viewBox="0 0 100 100">
              <defs>
                <radialGradient id="laxGrad">
                  <stop offset="0%" style={{ stopColor: theme.laxifier, stopOpacity: 0.9 }} />
                  <stop offset="70%" style={{ stopColor: theme.laxifier, stopOpacity: 0.4 }} />
                  <stop offset="100%" style={{ stopColor: theme.laxifier, stopOpacity: 0 }} />
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="45" fill="url(#laxGrad)" opacity="0.6"/>
              <circle cx="50" cy="50" r="35" fill="none" stroke={theme.laxifier} strokeWidth="4" opacity="0.9"/>
              <circle cx="50" cy="50" r="28" fill={theme.laxifier} opacity="0.3"/>
              <text x="50" y="60" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="bold" style={{ textShadow: '0 0 10px #000' }}>⚡ {laxifierHitsRef.current}/{laxifierBubble.hitsNeeded}</text>
            </svg>
          </div>
        )}

        {rampageBubble && (
          <div className="absolute" style={{ left: `${rampageBubble.x}%`, top: `${rampageBubble.y}%`, transform: 'translateX(-50%)' }}>
            <svg width={BUBBLE_SIZE} height={BUBBLE_SIZE} viewBox="0 0 100 100">
              <defs>
                <radialGradient id="rampageGrad">
                  <stop offset="0%" style={{ stopColor: theme.rampage, stopOpacity: 0.9 }} />
                  <stop offset="70%" style={{ stopColor: theme.rampage, stopOpacity: 0.4 }} />
                  <stop offset="100%" style={{ stopColor: theme.rampage, stopOpacity: 0 }} />
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="45" fill="url(#rampageGrad)" opacity="0.6"/>
              <circle cx="50" cy="50" r="35" fill="none" stroke={theme.rampage} strokeWidth="4" opacity="0.9"/>
              <circle cx="50" cy="50" r="28" fill={theme.rampage} opacity="0.3"/>
              <circle cx="35" cy="45" r="4" fill="#fff"/>
              <circle cx="50" cy="40" r="4" fill="#fff"/>
              <circle cx="65" cy="45" r="4" fill="#fff"/>
              <text x="50" y="70" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="bold" style={{ textShadow: '0 0 8px #000' }}>🔥</text>
            </svg>
          </div>
        )}

        {wrapBubble && (
          <div className="absolute" style={{ left: `${wrapBubble.x}%`, top: `${wrapBubble.y}%`, transform: 'translateX(-50%)' }}>
            <svg width={BUBBLE_SIZE} height={BUBBLE_SIZE} viewBox="0 0 100 100">
              <defs>
                <radialGradient id="wrapGrad">
                  <stop offset="0%" style={{ stopColor: theme.wrap, stopOpacity: 0.9 }} />
                  <stop offset="70%" style={{ stopColor: theme.wrap, stopOpacity: 0.4 }} />
                  <stop offset="100%" style={{ stopColor: theme.wrap, stopOpacity: 0 }} />
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="45" fill="url(#wrapGrad)" opacity="0.6"/>
              <circle cx="50" cy="50" r="35" fill="none" stroke={theme.wrap} strokeWidth="4" opacity="0.9"/>
              <circle cx="50" cy="50" r="28" fill={theme.wrap} opacity="0.3"/>
              <text x="50" y="62" textAnchor="middle" fill="#000" fontSize="28" fontWeight="bold" style={{ textShadow: '0 0 8px #fff' }}>🛡️</text>
            </svg>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes blast-expand {
          0% {
            width: 50px;
            height: 50px;
            opacity: 1;
          }
          100% {
            width: 800px;
            height: 800px;
            opacity: 0;
          }
        }
        
        @keyframes blast-particle {
          0% {
            transform: translate(-50%, -50%) translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) translate(calc(var(--angle-x) * 300px), calc(var(--angle-y) * 300px));
            opacity: 0;
          }
        }
        
        /* Wrap bubble color-changing animations */
        @keyframes wrap-color-cycle {
          0% { filter: hue-rotate(0deg) brightness(1.2); }
          25% { filter: hue-rotate(90deg) brightness(1.3); }
          50% { filter: hue-rotate(180deg) brightness(1.4); }
          75% { filter: hue-rotate(270deg) brightness(1.3); }
          100% { filter: hue-rotate(360deg) brightness(1.2); }
        }
        
        @keyframes wrap-pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        
        @keyframes wrap-shimmer {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.9; }
        }
        
        .wrap-bubble-container {
          animation: wrap-color-cycle 3s linear infinite;
        }
        
        .wrap-bubble-outer {
          animation: wrap-pulse 2s ease-in-out infinite;
        }
        
        .wrap-bubble-ring {
          animation: wrap-pulse 2s ease-in-out infinite;
        }
        
        .wrap-bubble-inner {
          animation: wrap-shimmer 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default SpaceLaxer;